import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  Home, 
  Radio, 
  Play, 
  Cast, 
  Settings, 
  Radio as LiveIcon,
  Users,
  User,
  LogOut,
  UserPlus,
  Menu,
  X
} from 'lucide-react';
import { useStream } from '../context/StreamContext';
import { useAuth } from '../context/AuthContext';

function Navbar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { activeStreams } = useStream();
  const { user, logout, isGuest } = useAuth();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const navLinks = [
    {
      name: 'Dashboard',
      path: '/',
      icon: Home,
    },
    {
      name: 'Host Stream',
      path: '/host',
      icon: Radio,
      requiresAuth: true,
      roles: ['host', 'admin', 'premium'],
    },
    {
      name: 'Watch Streams',
      path: '/viewer',
      icon: Play,
    },
    {
      name: 'Restream',
      path: '/restream',
      icon: Cast,
      requiresAuth: true,
      roles: ['host', 'admin', 'premium'],
    },
    {
      name: 'Invitations',
      path: '/invitations',
      icon: UserPlus,
      requiresAuth: true,
      roles: ['admin', 'moderator', 'host'],
    },
    {
      name: 'Settings',
      path: '/settings',
      icon: Settings,
    },
  ];

  const filteredNavLinks = navLinks.filter(link => {
    if (link.requiresAuth && isGuest()) {
      return false;
    }
    if (link.roles && !link.roles.includes(user?.role)) {
      return false;
    }
    return true;
  });

  return (
    <nav className="bg-gray-800 border-b border-gray-700 sticky top-0 z-40">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-cf-orange rounded-lg flex items-center justify-center">
              <span className="text-white text-sm font-bold">CS</span>
            </div>
            <span className="text-white text-xl font-bold">CloudStream</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-1">
            {filteredNavLinks.map((link) => {
              const IconComponent = link.icon;
              const isActive = location.pathname === link.path || 
                              (link.path === '/viewer' && location.pathname.startsWith('/viewer'));
              
              return (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`flex items-center space-x-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${
                    isActive
                      ? 'bg-cf-orange text-white'
                      : 'text-gray-300 hover:text-white hover:bg-gray-700'
                  }`}
                >
                  <IconComponent className="w-4 h-4" />
                  <span>{link.name}</span>
                </Link>
              );
            })}
          </div>

          {/* Right side items */}
          <div className="flex items-center space-x-4">
            {/* Live streams indicator */}
            {activeStreams.length > 0 && (
              <div className="hidden md:flex items-center space-x-2 px-3 py-1 bg-red-500 rounded-full">
                <LiveIcon className="w-4 h-4 text-white animate-pulse" />
                <span className="text-white text-sm font-medium">
                  {activeStreams.length} Live
                </span>
              </div>
            )}

            {/* User Menu */}
            <div className="relative">
              <button
                onClick={() => setShowUserMenu(!showUserMenu)}
                className="flex items-center space-x-2 p-1 rounded-lg hover:bg-gray-700 transition-colors duration-200"
              >
                <img
                  src={user?.avatar || `https://via.placeholder.com/32x32/0051c3/ffffff?text=${user?.displayName?.charAt(0) || 'U'}`}
                  alt="Avatar"
                  className="w-8 h-8 rounded-full object-cover"
                />
                <span className="hidden md:block text-white text-sm font-medium">
                  {user?.displayName || 'User'}
                </span>
                {isGuest() && (
                  <span className="hidden md:block px-2 py-1 bg-yellow-500 text-black text-xs rounded-full">
                    Guest
                  </span>
                )}
              </button>

              {/* User Dropdown */}
              {showUserMenu && (
                <div className="absolute right-0 mt-2 w-64 bg-gray-800 border border-gray-700 rounded-lg shadow-xl z-50">
                  <div className="p-4 border-b border-gray-700">
                    <div className="flex items-center space-x-3">
                      <img
                        src={user?.avatar || `https://via.placeholder.com/40x40/0051c3/ffffff?text=${user?.displayName?.charAt(0) || 'U'}`}
                        alt="Avatar"
                        className="w-10 h-10 rounded-full object-cover"
                      />
                      <div className="flex-1">
                        <p className="text-white font-medium">{user?.displayName}</p>
                        <p className="text-gray-400 text-sm">{user?.email || 'Guest User'}</p>
                        <div className="flex items-center space-x-2 mt-1">
                          <span className="px-2 py-1 bg-gray-700 text-gray-300 text-xs rounded-full capitalize">
                            {user?.role}
                          </span>
                          {isGuest() && (
                            <span className="px-2 py-1 bg-yellow-500 text-black text-xs rounded-full">
                              Guest
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="p-2">
                    {!isGuest() ? (
                      <>
                        <Link
                          to="/account"
                          onClick={() => setShowUserMenu(false)}
                          className="flex items-center space-x-2 w-full px-3 py-2 text-gray-300 hover:text-white hover:bg-gray-700 rounded-lg transition-colors duration-200"
                        >
                          <User className="w-4 h-4" />
                          <span>Account Settings</span>
                        </Link>
                        
                        <Link
                          to="/invitations"
                          onClick={() => setShowUserMenu(false)}
                          className="flex items-center space-x-2 w-full px-3 py-2 text-gray-300 hover:text-white hover:bg-gray-700 rounded-lg transition-colors duration-200"
                        >
                          <Users className="w-4 h-4" />
                          <span>Manage Invitations</span>
                        </Link>
                      </>
                    ) : (
                      <div className="px-3 py-2">
                        <p className="text-gray-400 text-sm mb-2">
                          Create a full account to access all features
                        </p>
                        <Link
                          to="/register"
                          className="block w-full text-center bg-cf-orange hover:bg-orange-600 text-white py-2 rounded-lg text-sm font-medium transition-colors duration-200"
                        >
                          Create Account
                        </Link>
                      </div>
                    )}
                    
                    <div className="border-t border-gray-700 my-2"></div>
                    
                    <button
                      onClick={handleLogout}
                      className="flex items-center space-x-2 w-full px-3 py-2 text-red-400 hover:text-red-300 hover:bg-gray-700 rounded-lg transition-colors duration-200"
                    >
                      <LogOut className="w-4 h-4" />
                      <span>Sign Out</span>
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Mobile menu button */}
            <button
              onClick={() => setShowMobileMenu(!showMobileMenu)}
              className="md:hidden p-2 text-gray-300 hover:text-white"
            >
              {showMobileMenu ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {showMobileMenu && (
          <div className="md:hidden py-4 border-t border-gray-700">
            <div className="space-y-2">
              {filteredNavLinks.map((link) => {
                const IconComponent = link.icon;
                const isActive = location.pathname === link.path || 
                                (link.path === '/viewer' && location.pathname.startsWith('/viewer'));
                
                return (
                  <Link
                    key={link.path}
                    to={link.path}
                    onClick={() => setShowMobileMenu(false)}
                    className={`flex items-center space-x-3 px-3 py-2 rounded-lg transition-colors duration-200 ${
                      isActive
                        ? 'bg-cf-orange text-white'
                        : 'text-gray-300 hover:text-white hover:bg-gray-700'
                    }`}
                  >
                    <IconComponent className="w-5 h-5" />
                    <span>{link.name}</span>
                  </Link>
                );
              })}
            </div>
          </div>
        )}
      </div>
      
      {/* Click outside to close menus */}
      {(showUserMenu || showMobileMenu) && (
        <div 
          className="fixed inset-0 z-30" 
          onClick={() => {
            setShowUserMenu(false);
            setShowMobileMenu(false);
          }}
        />
      )}
    </nav>
  );
}

export default Navbar; 